/**
 * /policies/loans/settlement-methods — Session 3c PR 8.
 * Owning agent: Mutaba'aa.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { session3cApi } from "../lib/session3c-api";
import type { SettlementMethodItem } from "../lib/session3c-api";
import { useLegalEntityId } from "../lib/legalEntity";
import { PolicyListPage } from "./_PolicyListPage";
import type { PolicyColumn } from "./_PolicyListPage";

export function LoanSettlementMethodsPolicyPage() {
  const { t } = useTranslation();
  const le = useLegalEntityId();

  const query = useQuery({
    queryKey: ["s3c-loan-settlement-methods", le],
    queryFn: () => session3cApi.listSettlementMethods(le ?? ""),
    enabled: !!le,
  });

  const items = query.data?.items ?? [];
  const counts = useMemo(() => ({
    total: items.length,
    attention: 0,
    critical: 0,
  }), [items]);

  const columns: PolicyColumn<SettlementMethodItem>[] = [
    {
      headerKey: "policies.loan_settlement.col.product",
      label: "Product",
      render: (r) => <span className="font-medium">{r.product_name_en}</span>,
    },
    {
      headerKey: "policies.loan_settlement.col.discount",
      label: "Early-payment discount %",
      render: (r) => <span className="font-mono">{r.early_payment_discount_pct.toFixed(2)}%</span>,
    },
    {
      headerKey: "policies.loan_settlement.col.arrears",
      label: "Arrears threshold (installments)",
      render: (r) => <span className="font-mono text-xs">{r.arrears_escalation_threshold}</span>,
    },
    {
      headerKey: "policies.loan_settlement.col.writeoff",
      label: "Auto-write-off threshold",
      render: (r) => <span className="font-mono text-xs">{r.auto_writeoff_threshold ?? "—"}</span>,
    },
  ];

  return (
    <PolicyListPage<SettlementMethodItem>
      title={t("policies.loan_settlement.title", "Loan Settlement Methods")}
      subtitle={t("policies.loan_settlement.subtitle", "Early-settlement formulas · arrears escalation thresholds.")}
      agent="mutabaa"
      domain="loan_settlement"
      newButtonLabel={t("policies.loan_settlement.cta.new", "New Settlement Rule")}
      newButtonMode="coming_soon"
      newButtonExpectedRelease="M3X.WORKFLOW"
      query={query}
      columns={columns}
      rowKey={(r) => r.id}
      suggestions={[
        {
          headline: t("policies.loan_settlement.suggestion.clean_headline", `${items.length} rule${items.length === 1 ? "" : "s"} loaded.`),
          body: t("policies.loan_settlement.suggestion.clean_body", "Early-payment discount % + arrears escalation threshold. Auto-write-off path documented in the M3X.LOANS pack."),
          primaryActionLabel: t("policies.loan_settlement.suggestion.audit", "Open audit log"),
          primaryActionOnClick: () => window.location.assign("/settings/audit-log"),
          suggestionKey: "loan-settlement-methods-brief",
        },
      ]}
      briefContext={{
        total: counts.total,
        attention: counts.attention,
        critical: counts.critical,
        top_kind: t("policies.loan_settlement.title", "Loan Settlement Methods"),
        window_label: t("policies.loan_settlement.window", "across products"),
      }}
    />
  );
}
